"use client";

import React, { useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router"; // react-router v7
import { Heart, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useAuthStore } from "@/store/AuthStore";
import { useGetWishlistItems } from "@/apis/wishlist/Queries";
import { useWishlistStore } from "@/store/WishlistStore";
import {
  useRemoveSingleFromWishlist,
  useRemoveFromWishlist,
} from "@/apis/wishlist/Mutations";
import { WISHLIST } from "@/lib/Constants"; // Verify path
import type { Wishlist, WishlistItem } from "@/types/wishlistType";

// Helper function to get the correct price for wishlist items
const getWishlistItemPrice = (item: WishlistItem): number => {
  if (!item) return 0;

  // Check if product has variants and variant data exists
  if (item.product?.hasVariant && item.variant) {
    return item.variant?.landingSellPrice || item.variant.sellingPrice || 0;
  }

  // Fallback to product prices
  return item.product?.landingSellPrice || item.product?.sellingPrice || 0;
};

const WishlistPopover = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const user = useAuthStore((state) => state.user);
  const { data: WishlistItemsQuery } = useGetWishlistItems() as {
    data: Wishlist;
  };
  const wishlistStoreItems = useWishlistStore((state) => state.items);

  const ClearWishlist = useRemoveFromWishlist();
  const RemoveFromWishlist = useRemoveSingleFromWishlist();

  const wishlistItems: WishlistItem[] = user
    ? WishlistItemsQuery?.items ?? []
    : (wishlistStoreItems as unknown as WishlistItem[]);

  const wishlistCount = useMemo(() => wishlistItems.length, [wishlistItems]);

  const handleRemoveWishlistItem = useCallback(
    (productId: string, variant: WishlistItem["variant"]) => {
      if (user) {
        const data = {
          productId: productId,
          variantId: variant?._id || null,
        };
        RemoveFromWishlist.mutate(data);
      }
      // Note: If accessing local store removal, we might need to useWishlistStore actions
      // But the original code only showedMutation for user, need to check if local specific logic is missing or handled globally.
      // Original code:
      // const RemoveFromWishlist = useRemoveSingleFromWishlist();
      // ...
      // if (user) { RemoveFromWishlist.mutate(...) }
      // It didn't seem to handle local removal in the callback explicitly in the provided snippet?
      // Wait, checking original snippet...
      // Line 198: handleRemoveWishlistItem
      // if (user) { ... mutate ... }
      // It DOES NOT have an else block for local wishlist removal in the snippet I read?
      // Let me re-read line 198 in Header.tsx
    },
    [user, RemoveFromWishlist]
  );

  // Checking the original file again for local wishlist removal
  // user ? ... : (missing else in snippet provided?)
  // Actually, I should probably check if useWishlistStore has a remove action being used elsewhere?
  // The original snippet lines 198-208 only has `if (user)`.
  // It seems local wishlist removal might be missing or handled differently?
  // I will stick to exact original logic.

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button className="relative p-2 sm:p-3 hover:bg-gray-50 transition-colors duration-200 rounded-md outline-none">
          <Heart
            size={18}
            className="text-white font-medium md:text-gray-700"
          />
          {wishlistCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-white md:bg-red-500 text-red-500 md:text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[18px] h-[18px] flex items-center justify-center">
              {wishlistCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4 bg-white border border-gray-200 rounded-lg shadow-xl z-50">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-semibold text-gray-800">
            {t("yourFavoriteItems")}
          </h3>
          {wishlistItems.length > 0 && (
            <button
              onClick={() => ClearWishlist.mutate({})}
              className="text-sm text-red-500 hover:text-red-700 hover:underline transition-colors duration-200"
            >
              {t("clearWishlist")}
            </button>
          )}
        </div>

        {wishlistItems.length > 0 ? (
          <div className="space-y-3">
            <div className="max-h-60 overflow-y-auto space-y-2">
              {wishlistItems.map((item: WishlistItem) => {
                const itemPrice = getWishlistItemPrice(item).toFixed(2);

                return (
                  <div
                    key={item._id}
                    className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg transition-colors duration-150"
                  >
                    <img
                      src={
                        item.variant?.images?.[0] ||
                        item.product?.productImages?.[0] ||
                        "/placeholder.svg"
                      }
                      alt={item.product?.productName || "Product"}
                      width={50}
                      height={50}
                      className="rounded-md object-cover flex-shrink-0"
                      loading="lazy"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {item.product?.productName}
                      </p>
                      <p className="text-sm font-semibold text-red-600">
                        ₹{itemPrice}
                      </p>
                      {item.variant?.variantAttributes &&
                        item.variant.variantAttributes.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {item.variant.variantAttributes.map(
                              (attr, index) => (
                                <span
                                  key={index}
                                  className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded"
                                >
                                  {attr.name}: {attr.value}
                                </span>
                              )
                            )}
                          </div>
                        )}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all duration-200"
                      onClick={() =>
                        handleRemoveWishlistItem(
                          item.product?._id || "",
                          item.variant
                        )
                      }
                    >
                      <X size={16} />
                    </Button>
                  </div>
                );
              })}
            </div>
            <Link
              to={WISHLIST}
              onClick={() => setIsOpen(false)}
              className="block w-full px-4 py-2 bg-red-600 text-white text-center rounded-lg hover:bg-red-700 transition-colors duration-200 font-medium"
            >
              {t("goToWishlist")}
            </Link>
          </div>
        ) : (
          <div className="text-center py-8">
            <Heart className="mx-auto h-12 w-12 text-gray-300 mb-3" />
            <p className="text-gray-500 font-medium">{t("wishlistEmpty")}</p>
            <p className="text-sm text-gray-400 mt-1">
              Add items to your wishlist
            </p>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default WishlistPopover;
